import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { SEC_ApiCall } from '../func.js';
import LoadingComp from '../common/loading.js';
import { ApiError } from '../common/apiError.js';
import { AssignUserWithNote } from '../common/toolbar/AssignUserWithNote.js';
import { NoteInputComponent } from '../common/toolbar/NoteInputComponent.js';
import { AssignUser } from '../common/toolbar/AssignUser.js';
import { AddNote } from '../common/toolbar/AddNote.js';
import { MarkCompleteButton } from '../common/toolbar/MarkCompleteButton.js';
import { MarkIncompleteButton } from '../common/toolbar/MarkIncompleteButton.js';
import { UpdateStatusButton } from '../common/toolbar/UpdateStatusButton.js';
import { UpdateStatusSelect } from '../common/toolbar/UpdateStatusSelect.js';
import { RequestFilesList } from '../common/RequestFilesList.js';
import { ExpandableLinkButton } from '../common/ExpandableLinkButton.js';

/**
 * Full Reprographics request view for either managers or ownership-scoped requesters.
 */
export default function ReproRequestAdminSingleView({ requesterMode = false }) {
  const navigate = useNavigate();
  const [modalState, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case 'SET_MODAL':
        return {
          ...state,
          active: true,
          apiState: action.payload.apiState,
          message: action.payload.message || '',
        };
      default:
        return state;
    }
  }, { active: false, apiState: '', message: '' });

  const [dataState, setDataState] = useState(null);
  const [requestIdState, setRequestIdState] = useState(null);
  const [assignableUsersState, setAssignableUsersState] = useState([]);
  const [statusOptionsState, setStatusOptionsState] = useState([]);
  const [isLoadedState, setIsLoadedState] = useState(false);
  const [apiErrorState, setApiErrorState] = useState(false);
  const [activePanel, setActivePanel] = useState(null);

  const statusValue = useMemo(() => {
    if (dataState?.status && typeof dataState.status === 'object') {
      return dataState.status.name || 'Open';
    }
    if (dataState?.status) {
      return dataState.status;
    }
    return dataState?.completed === 1 ? 'Complete' : 'Open';
  }, [dataState]);

  const statusColour = useMemo(() => {
    if (dataState?.status && typeof dataState.status === 'object' && dataState.status.colour) {
      return dataState.status.colour;
    }
    return '#f9b233';
  }, [dataState]);

  const statusTextColour = useMemo(() => {
    if (dataState?.status && typeof dataState.status === 'object' && dataState.status.textColour) {
      return dataState.status.textColour;
    }
    return '#ffffff';
  }, [dataState]);

  const statusSelectOptions = useMemo(() => {
    if (!Array.isArray(statusOptionsState)) {
      return [];
    }
    const currentStatusId =
      dataState?.status && typeof dataState.status === 'object'
        ? dataState.status.id
        : dataState?.status;
    return statusOptionsState
      .filter((status) => status?.id && status.id !== currentStatusId)
      .map((status) => ({
        value: status.id,
        label: status.name,
      }));
  }, [statusOptionsState, dataState]);

  const isComplete = dataState?.completed === 1 || statusValue === 'Complete';

  async function apiCall(requestId) {
    try {
      const endpoint = requesterMode ? 'repro/user/single' : 'repro/admin/single';
      const response = await SEC_ApiCall(endpoint, { requestId }, setApiErrorState);
      const parsedResponse = normaliseSingleResponse(response);
      setAssignableUsersState(Array.isArray(parsedResponse?.reproAssignableUsers) ? parsedResponse.reproAssignableUsers : []);
      setStatusOptionsState(Array.isArray(parsedResponse?.reproRequestStatuses) ? parsedResponse.reproRequestStatuses : []);
      setDataState(normaliseRequestData(parsedResponse?.data || null));
      setIsLoadedState(true);
      setActivePanel(null);
    } catch (error) {
      console.error('Failed to load Reprographics request', error);
      setIsLoadedState(true);
      setApiErrorState(true);
    }
  }

  useEffect(() => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const id = params.get('id');
    setRequestIdState(id);
    if (id) {
      apiCall(id);
    } else {
      setIsLoadedState(true);
      setApiErrorState(true);
    }
  }, []);

  function handlePanelToggle(panel) {
    if (activePanel === panel) {
      setActivePanel(null);
      return;
    }
    setActivePanel(panel);
  }

  if (!isLoadedState) {
    return (
      <div className='main_div'>
        <LoadingComp />
      </div>
    );
  }

  if (apiErrorState || !dataState) {
    return <ApiError />;
  }

  return (
    <div className='internal_main_div repro-single-shell'>
      <div className="repro-single-header">
        <button
          type="button"
          className="repro-single-back-button"
          onClick={() => navigate(requesterMode ? '/app/repro/user/requests' : '/app/repro/admin/requests')}
        >
          <FaArrowLeft />
          Back
        </button>
        <div>
          <p className="repro-single-kicker">Reprographics Request #{dataState.requestId}</p>
          <h1 className="repro-single-title">{dataState.requestTitle || 'Untitled request'}</h1>
        </div>
        <span
          className="repro-single-status-pill"
          style={{ background: statusColour, color: statusTextColour }}
        >
          {statusValue}
        </span>
      </div>

      <div className="repro-single-actions">
        {!requesterMode && !isComplete && (
          <AssignUser
            onClick={() => handlePanelToggle('assign')}
            isActive={activePanel === 'assign'}
          />
        )}
        <AddNote
          onClick={() => handlePanelToggle('note')}
          isActive={activePanel === 'note'}
        />
        {!requesterMode && (
          <UpdateStatusButton
            onClick={() => handlePanelToggle('status')}
            isActive={activePanel === 'status'}
          />
        )}
        {isComplete ? (
          <MarkIncompleteButton
            onClick={() => handlePanelToggle('incomplete')}
            isActive={activePanel === 'incomplete'}
          />
        ) : (
          <MarkCompleteButton
            onClick={() => handlePanelToggle('complete')}
            isActive={activePanel === 'complete'}
          />
        )}
      </div>

      {activePanel && (
        <div className="repro-single-action-panel">
          {!requesterMode && activePanel === 'assign' && (
            assignableUsersState.length > 0 ? (
              <AssignUserWithNote
                usersArray={assignableUsersState}
                requestId={dataState.requestId}
                url="repro/SingleRequest/assign"
                updateApiCallFunction={() => apiCall(dataState.requestId)}
                setActiveOption={() => setActivePanel(null)}
                cardView
                showPrivateToggle={!requesterMode}
                uploadModuleType="repro"
                permissionsKey="reproRequestsData"
              />
            ) : (
              <p className="repro-single-muted">No assignable Reprographics users found.</p>
            )
          )}
          {activePanel === 'note' && (
            <NoteInputComponent
              requestId={dataState.requestId}
              url="repro/SingleRequest/notes"
              updateApiCallFunction={() => apiCall(dataState.requestId)}
              placeholder="Enter your note here..."
              cardView
              showPrivateToggle={!requesterMode}
              uploadModuleType="repro"
              permissionsKey="reproRequestsData"
            />
          )}
          {!requesterMode && activePanel === 'status' && (
            <UpdateStatusSelect
              requestId={dataState.requestId}
              url="repro/SingleRequest/status"
              updateApiCallFunction={() => apiCall(dataState.requestId)}
              setActiveOption={() => setActivePanel(null)}
              cardView
              options={statusSelectOptions}
            />
          )}
          {activePanel === 'complete' && (
            <NoteInputComponent
              requestId={dataState.requestId}
              url="repro/SingleRequest/complete"
              updateApiCallFunction={() => apiCall(dataState.requestId)}
              placeholder="Enter your completion note..."
              cardView
              showPrivateToggle={!requesterMode}
              uploadModuleType="repro"
              permissionsKey="reproRequestsData"
            />
          )}
          {activePanel === 'incomplete' && (
            <NoteInputComponent
              requestId={dataState.requestId}
              url="repro/SingleRequest/incomplete"
              updateApiCallFunction={() => apiCall(dataState.requestId)}
              placeholder="Enter your reason for reopening..."
              cardView
              showPrivateToggle={!requesterMode}
              uploadModuleType="repro"
              permissionsKey="reproRequestsData"
            />
          )}
        </div>
      )}

      <div className="repro-single-grid">
        <section className="repro-single-panel">
          <h2 className="repro-single-panel-title">Request Details</h2>
          <dl className="repro-single-detail-list">
            <DetailRow label="Submitted by" value={dataState.submittingUser || 'Unknown'} />
            <DetailRow label="Submitted on" value={formatDateTime(dataState.submissionTimestamp)} />
            {dataState.assignedUser && dataState.assignedUser !== 'None' && (
              <DetailRow label="Assigned to" value={dataState.assignedUser} />
            )}
            <DetailRow label="Required date" value={formatDate(dataState.requiredDate)} />
            <DetailRow label="Copies" value={dataState.copyCount || 0} />
          </dl>
          <div className="repro-single-description">
            <h3>Instructions</h3>
            <p>{dataState.otherInstructions || 'No additional instructions'}</p>
          </div>
        </section>

        <section className="repro-single-panel">
          <h2 className="repro-single-panel-title">Print Specification</h2>
          <dl className="repro-single-detail-list">
            <DetailRow label="Paper size" value={dataState.paperSize || 'Not set'} />
            <DetailRow label="Paper colour" value={formatReproOptionValue(dataState.paperColour)} />
            <DetailRow label="Print" value={formatReproOptionValue(dataState.printColour)} />
            <DetailRow label="Material" value={formatReproOptionValue(dataState.material)} />
            <DetailRow label="Sides" value={formatReproOptionValue(dataState.sides)} />
            <DetailRow label="Staples" value={formatReproOptionValue(dataState.staples)} />
            <DetailRow label="Binding" value={formatReproOptionValue(dataState.binding)} />
          </dl>
        </section>

        <section className="repro-single-panel">
          <h2 className="repro-single-panel-title">Files and Links</h2>
          <RequestFilesList
            files={dataState.files}
            folder="repro"
            itemId={requestIdState}
            title="Attached Files"
            emptyText="No files attached."
            className="repro-single-file-list"
            dispatch={dispatch}
          />

          {Array.isArray(dataState.links) && dataState.links.length > 0 ? (
            <div className="repro-single-link-list">
              <h3>Links</h3>
              <p className="repro-single-link-warning">
                School Engine does not check or verify link contents. Only open links you trust.
              </p>
              {dataState.links.map((link, index) => (
                <ExpandableLinkButton link={link} key={`${link.url}-${index}`} />
              ))}
            </div>
          ) : (
            <p className="repro-single-muted">No links added.</p>
          )}
        </section>

        <section className="repro-single-panel repro-single-notes-panel">
          <h2 className="repro-single-panel-title">Notes</h2>
          {Array.isArray(dataState.requestEvents?.additionalEvents) && dataState.requestEvents.additionalEvents.length > 0 ? (
            <div className="repro-single-notes-list">
              {dataState.requestEvents.additionalEvents.map((event, eventIndex) => (
                <article
                  key={`${event.timestamp || 'event'}-${eventIndex}`}
                  className={`repro-single-note${event?.privateNote ? ' repro-single-note-private' : ''}`}
                >
                  <div className="repro-single-note-meta">
                    <span>{event.user || 'Unknown'}</span>
                    <span>{formatDateTime(event.timestamp)}</span>
                  </div>
                  <p>{event.eventData || 'No note text'}</p>
                  {event?.subNote && <p className="repro-single-note-sub">Note: {event.subNote}</p>}
                  {Array.isArray(event.files) && event.files.length > 0 && (
                    <div className="repro-single-note-files">
                      <RequestFilesList
                        files={event.files}
                        folder="repro"
                        itemId={requestIdState}
                        title="Files"
                        groupKey={eventIndex}
                        dispatch={dispatch}
                      />
                    </div>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <p className="repro-single-muted">No notes have been added.</p>
          )}
        </section>
      </div>

      {modalState.active && (
        <div className={`toast ${modalState.apiState}`}>
          <span>{modalState.message}</span>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="repro-single-detail-row">
      <dt>{label}</dt>
      <dd>{value || 'Not set'}</dd>
    </div>
  );
}

function normaliseSingleResponse(response) {
  if (!response) {
    return {};
  }
  if (typeof response === 'string') {
    try {
      return JSON.parse(response);
    } catch (_) {
      return {};
    }
  }
  return response;
}

function normaliseRequestData(data) {
  if (!data || typeof data !== 'object') {
    return null;
  }
  return {
    ...data,
    files: normaliseList(data.files),
    links: normaliseList(data.links),
    requestEvents: normaliseEvents(data.requestEvents),
  };
}

function normaliseList(value) {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }
  return [];
}

function normaliseEvents(value) {
  let events = value;
  if (typeof events === 'string') {
    try {
      events = JSON.parse(events);
    } catch (_) {
      events = {};
    }
  }
  if (!events || typeof events !== 'object') {
    events = {};
  }
  return {
    primaryEvent: events.primaryEvent || {},
    additionalEvents: Array.isArray(events.additionalEvents) ? events.additionalEvents : [],
  };
}

function formatDate(rawValue) {
  if (!rawValue) {
    return 'Not set';
  }
  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) {
    return rawValue;
  }
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(parsed);
}

function formatDateTime(rawValue) {
  if (!rawValue) {
    return 'Not set';
  }
  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) {
    return rawValue;
  }
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

function formatReproOptionValue(value) {
  const displayValues = {
    blackWhite: 'Black and white',
    colour: 'Colour',
    singleSided: 'Single sided',
    doubleSided: 'Double sided',
    cornerSingle: 'Corner single',
    edgesDouble: 'Edges double',
    none: 'None',
    yes: 'Yes',
    no: 'No',
  };
  if (displayValues[value]) {
    return displayValues[value];
  }
  if (!value) {
    return 'Not set';
  }
  return String(value).replace(/([a-z])([A-Z])/g, '$1 $2');
}
